// @ts-check
/**
 * Shared configuration and helpers for the Business View reporting page.
 *
 * RAG (Red/Amber/Green) banding maps a flow's non-passing ratio to a status.
 * Colours are pinned to the existing metriQ theme tokens (see src/index.css):
 *   at_risk -> red-500
 *   monitor -> orange-500
 *   healthy -> green-500
 */

/**
 * 'unavailable' is not a RAG band: it means a required test type did not
 * complete, so the flow has no counts to band. It is rendered neutrally.
 * @typedef {'at_risk' | 'monitor' | 'healthy' | 'unavailable'} RagStatus
 */

/**
 * @typedef {Object} RagMeta
 * @property {RagStatus} status
 * @property {string} label           Human readable label ("At Risk").
 * @property {string} phrase          The label as it reads mid-sentence ("at risk").
 * @property {string} chipClass       Tailwind classes for the status chip: light fill, black text. The status
 *                                    colour sits on the dot, never on the label, so the text clears WCAG AA.
 * @property {string} textClass       Tailwind text colour for a status heading.
 * @property {string} iconBorderClass Tailwind border colour for the status icon's box.
 * @property {string} dotClass        Tailwind background class for a status dot.
 * @property {string} hex             Concrete colour for SVG/chart fills.
 */

/**
 * The chip is a soft pill, not a solid block: solid on the theme's yellow (#ffcf01) has
 * almost no contrast whichever text colour it is given.
 *
 * Pill and heading colours use Tailwind's own 50/200/400/700 shades rather than the -500
 * tokens, which index.css redefines. The -500 tokens stay on dots and chart fills, where
 * they are read as colour rather than against text.
 *
 * @type {Record<RagStatus, RagMeta>}
 */
export const RAG_META = {
  at_risk: {
    status: 'at_risk',
    label: 'At Risk',
    phrase: 'at risk',
    chipClass: 'bg-pink-100 text-black',
    textClass: 'text-pink-500 dark:text-red-300',
    iconBorderClass: 'border-pink-500 dark:border-red-700',
    dotClass: 'bg-pink-500',
    hex: '#de5d70',
  },
  monitor: {
    status: 'monitor',
    label: 'Monitor',
    phrase: 'one to monitor',
    chipClass: 'bg-orange-100 text-black',
    textClass: 'text-orange-500 dark:text-amber-300',
    iconBorderClass: 'border-orange-500 dark:border-amber-700',
    dotClass: 'bg-orange-500',
    hex: '#ff9800',
  },
  healthy: {
    status: 'healthy',
    label: 'Healthy',
    phrase: 'healthy',
    chipClass: 'bg-green-100 text-black',
    textClass: 'text-green-500 dark:text-green-300',
    iconBorderClass: 'border-green-500 dark:border-green-700',
    dotClass: 'bg-green-500',
    hex: 'var(--pass-green)',
  },
  unavailable: {
    status: 'unavailable',
    label: 'Unavailable',
    phrase: 'unavailable',
    chipClass: 'bg-gray-100 text-black',
    textClass: 'text-gray-700 dark:text-gray-300',
    iconBorderClass: 'border-gray-300 dark:border-gray-600',
    dotClass: 'bg-gray-400',
    hex: '#9CA3AF', // gray-400
  },
};

/**
 * Order used for legends, KPI cards and default sorting (worst first).
 * 'unavailable' is deliberately excluded: it is not a band, so it is neither
 * stacked in the trend chart nor given a KPI card.
 */
export const RAG_ORDER = /** @type {RagStatus[]} */ (['at_risk', 'monitor', 'healthy']);

/**
 * Rank used to sort flows worst-first. Anything unrecognised sorts last.
 * @param {string} [status]
 * @returns {number}
 */
export const getRagRank = (status) => {
  const rank = RAG_ORDER.indexOf(/** @type {RagStatus} */ (status));
  return rank === -1 ? RAG_ORDER.length : rank;
};

/**
 * Resolve the display metadata for a RAG status. Unknown statuses fall back to
 * 'unavailable' rather than 'healthy' so a missing or new status is never
 * reported as green.
 * @param {string} [status]
 * @returns {RagMeta}
 */
export const getRagMeta = (status) => RAG_META[/** @type {RagStatus} */ (status)] || RAG_META.unavailable;

/**
 * Text classes for the failed / total counts on the flows table. Each clears WCAG AA on white;
 * the gray-500 and green-500 they replace did not.
 */
export const COUNT_CLASS = {
  failed: 'text-red-600 font-bold',
  clean: 'text-green-800 font-bold',
  muted: 'text-gray-700',
};

/**
 * Join values into a readable list ("financial, reputational and operational").
 * Intl.ListFormat is not in every runtime the tests use, so a plain join backs it up.
 *
 * @param {string[]} [values]
 * @returns {string}
 */
export const formatList = (values = []) => {
  const items = (values ?? []).filter(Boolean);

  if (typeof Intl?.ListFormat === 'function') {
    return new Intl.ListFormat('en-GB', { style: 'long', type: 'conjunction' }).format(items);
  }

  return items.length > 1 ? `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}` : items.join('');
};

/**
 * Format a numeric amount as whole GBP (e.g. 16000 -> "£16,000").
 * @param {number} amount
 * @returns {string}
 */
export const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0,
  }).format(amount ?? 0);

/* -------------------------------------------------------------------------- */
/* Linked test results (flow detail view)                                      */
/* -------------------------------------------------------------------------- */

/**
 * Normalised outcome of a single linked test, as stored by the calculation.
 * Mirrors BusinessViewStoredNormalisedResult in @disqo/shared: the six outcomes a
 * result item can carry, plus 'not_found' for a configured test that had no
 * matching item in an otherwise usable result.
 * @typedef {'passed'|'failed'|'errored'|'skipped'|'not_executed'|'not_found'|'other'} TestResult
 */

/**
 * @typedef {Object} TestResultMeta
 * @property {string} label         Human readable label ("Not Executed").
 * @property {string} textClass     Tailwind text colour class for the Result column.
 * @property {string} borderClass   Tailwind colour class for the row's left edge. Side-specific
 *                                  (border-l-*) deliberately: index.css gives the plain
 *                                  .border-pink-500 / .border-green-500 a `color: !important`,
 *                                  which would tint the cell's text as well as its edge.
 * @property {string} dotClass      Tailwind background class for a legend dot.
 * @property {string} chartColour   Tremor colour name for the pass-rate donut.
 * @property {boolean} isPassing    Only 'passed' passes; everything else counts against the flow.
 * @property {boolean} isFailure    A genuine failure of the test, as opposed to it not running.
 */

/**
 * Display metadata for every result a linked test can report.
 *
 * Three bands: 'passed' is green, the two genuine failures ('failed', 'errored') are pink, and
 * everything else - the results meaning the test produced no verdict - is orange. Reporting
 * "did not run" as a failure would overstate it and as a pass would hide it.
 *
 * textClass and borderClass are held separately rather than derived from one another because
 * Tailwind only emits classes it can see written out in full; a composed `text-${colour}-500`
 * would be purged from the build.
 *
 * @type {Record<TestResult, TestResultMeta>}
 */
export const TEST_RESULT_META = {
  passed: {
    label: 'Passed',
    textClass: 'text-green-800',

    borderClass: 'border-l-green-500',
    dotClass: 'bg-green-500',
    chartColour: 'green',
    isPassing: true,
    isFailure: false,
  },
  failed: {
    label: 'Failed',
    textClass: 'text-pink-700',

    borderClass: 'border-l-pink-500',
    dotClass: 'bg-pink-500',
    chartColour: 'pink',
    isPassing: false,
    isFailure: true,
  },
  errored: {
    label: 'Error',
    textClass: 'text-pink-700',

    borderClass: 'border-l-pink-500',
    dotClass: 'bg-pink-500',
    chartColour: 'red',
    isPassing: false,
    isFailure: true,
  },
  skipped: {
    label: 'Skipped',
    textClass: 'text-gray-700',

    borderClass: 'border-l-orange-500',
    dotClass: 'bg-orange-500',
    chartColour: 'orange',
    isPassing: false,
    isFailure: false,
  },
  not_executed: {
    label: 'Not Executed',
    textClass: 'text-gray-700',

    borderClass: 'border-l-orange-500',
    dotClass: 'bg-orange-500',
    chartColour: 'orange',
    isPassing: false,
    isFailure: false,
  },
  not_found: {
    label: 'Not Found',
    textClass: 'text-gray-700',

    borderClass: 'border-l-orange-500',
    dotClass: 'bg-orange-500',
    chartColour: 'orange',
    isPassing: false,
    isFailure: false,
  },
  other: {
    label: 'Other',
    textClass: 'text-gray-700',

    borderClass: 'border-l-gray-400',
    dotClass: 'bg-gray-400',
    chartColour: 'gray-700',
    isPassing: false,
    isFailure: false,
  },
};

/**
 * Results worst first, passing last: what is wrong with a flow is what the reader
 * came for, so a failure never sits below a pass. Used to order the linked tests
 * table.
 */
export const TEST_RESULT_SEVERITY_ORDER = /** @type {TestResult[]} */ ([
  'failed',
  'errored',
  'skipped',
  'not_executed',
  'not_found',
  'other',
  'passed',
]);

/**
 * Order results are listed in for the pass-rate donut and its legend — passed
 * leads, as the chart reads it, then worst first. Derived from the severity order
 * so the two cannot drift apart.
 */
export const TEST_RESULT_ORDER = /** @type {TestResult[]} */ ([
  'passed',
  ...TEST_RESULT_SEVERITY_ORDER.filter((result) => result !== 'passed'),
]);

/**
 * Sort position of a result, worst first. An unrecognised result ranks with
 * 'other' rather than last, so a result the API adds later still sorts above the
 * passes it is not one of.
 *
 * @param {string} [result]
 * @returns {number}
 */
export const getResultRank = (result) => {
  const rank = TEST_RESULT_SEVERITY_ORDER.indexOf(/** @type {TestResult} */ (result));
  return rank === -1 ? TEST_RESULT_SEVERITY_ORDER.indexOf('other') : rank;
};

/**
 * Turn a snake_case API value into a readable label ("not_executed" -> "Not Executed").
 * @param {string} value
 * @returns {string}
 */
export const humaniseValue = (value) =>
  String(value ?? '')
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

/**
 * Resolve the display metadata for a linked test result. An unrecognised one is labelled
 * from its own value and never treated as passing, so a result the API adds later is not
 * silently counted as a pass.
 *
 * @param {string} [result]
 * @returns {TestResultMeta}
 */
export const getResultMeta = (result = 'other') =>
  TEST_RESULT_META[/** @type {TestResult} */ (result)] || {
    ...TEST_RESULT_META.other,
    label: humaniseValue(result) || TEST_RESULT_META.other.label,
  };

/**
 * Short label for a test type, for the compact coverage mix ("3 UI · 2 API").
 * Keyed on the numeric test_type_id (see TEST_TYPE_IDS in DataUtils).
 * @type {Record<number, string>}
 */
export const SHORT_TEST_TYPE_LABELS = {
  1: 'Code',
  2: 'API',
  3: 'UI',
  4: 'Perf',
  5: 'A11y',
  6: 'Compat',
};

/**
 * Route of the detail view for one flow. Kept here so the flows table link and the
 * route registration cannot drift apart.
 * @param {string|number} projectId
 * @param {string|number} workflowId
 * @returns {string}
 */
export const buildFlowDetailPath = (projectId, workflowId) =>
  `/business-view/${projectId}/flows/${workflowId}`;

/**
 * Route of the flows overview for one project.
 * @param {string|number} [projectId]
 * @returns {string}
 */
export const buildOverviewRoute = (projectId) =>
  projectId ? `/business-view/${projectId}` : '/business-view';
